// =============================================================================
// line-settings.js – Per-line configuration UI rendering
// =============================================================================
// Extracted from settings.js. Handles the per-line settings panel within the
// Settings "Fermate" subtab: favorite stops, timetable columns, direction/day.
// =============================================================================

import { getScheduleKey, escapeHtml } from "./utils.js";
import { getStopName } from "./line-config.js";
import { resolveFavoriteStop } from "./live-logic";

/**
 * Safely render per-line settings, catching errors gracefully.
 */
export function safeRenderLineSettings(lineId, state, cfg, lineData, lineConfig) {
  try {
    return renderLineSettings(lineId, state, cfg, lineData, lineConfig);
  } catch (e) {
    console.error(`[Trasporti] Errore nel render impostazioni linea ${lineId}:`, e);
    return `<div class="empty-mini" style="border-color: rgba(239,68,68,0.3); color: #fecaca;">Errore nel caricamento delle impostazioni per ${lineId}.</div>`;
  }
}

/**
 * Render per-line settings panel HTML.
 */
export function renderLineSettings(lineId, state, cfg, lineData, lineConfig) {
  const profile = cfg.stopProfiles?.[lineId] || {};
  const favoriteOutbound = resolveFavoriteStop(lineId, "outbound", state, cfg);
  const favoriteReturn = resolveFavoriteStop(lineId, "return", state, cfg);
  const outboundCandidates = collectCandidates(
    profile.outboundHomeStops,
    profile.detailStops?.outbound,
    profile.compactStops?.outbound,
    lineConfig[lineId]?.referenceStops
  );
  const returnCandidates = collectCandidates(
    profile.returnHomeStops,
    profile.returnInterchanges,
    profile.detailStops?.return,
    profile.compactStops?.return
  );
  const direction = state.settingsLineDirection || "outbound";
  const dayType = state.settingsLineDay || "weekday";
  const scheduleKey = getScheduleKey(lineId, dayType, direction);
  const allStops = collectStopsFromTrips(lineData[lineId]?.[scheduleKey] || []);
  const selectedStops =
    state.settings?.timetableStops?.[lineId]?.[scheduleKey] ||
    profile.timetableStops?.[scheduleKey] ||
    profile.timetableStops?.[direction] ||
    allStops.slice(0, 4);

  return `
    <div class="settings-grid two">
      <label class="field-row">
        <span>Preferita Andata</span>
        <select data-favorite-stop="${lineId}:outbound">
          ${renderStopOptions(outboundCandidates, favoriteOutbound)}
        </select>
      </label>
      <label class="field-row">
        <span>Preferita Ritorno</span>
        <select data-favorite-stop="${lineId}:return">
          ${renderStopOptions(returnCandidates, favoriteReturn)}
        </select>
      </label>
    </div>

    <div class="filter-row">
      ${["weekday", "saturday", "sunday"]
        .map((dt) => {
          const key = getScheduleKey(lineId, dt, direction);
          const disabled = !lineData[lineId]?.[key]?.length;
          return `<button type="button" data-settings-day="${dt}" class="${dayType === dt ? "active" : ""}" ${disabled ? "disabled" : ""}>${labelDay(dt)}</button>`;
        })
        .join("")}
    </div>
    <div class="segmented wide">
      <button type="button" data-settings-dir="outbound" class="${direction === "outbound" ? "active" : ""}">Andata</button>
      <button type="button" data-settings-dir="return" class="${direction === "return" ? "active" : ""}">Ritorno</button>
    </div>

    <div class="checkbox-panel">
      <div class="checkbox-panel-head">
        <strong>Fermate visibili in Orari</strong>
        <button type="button" class="text-btn" data-reset-line-stops="${lineId}:${scheduleKey}">default</button>
      </div>
      <div class="checkbox-grid">
        ${
          allStops
            .map(
              (code) => `<label class="check-row">
          <input type="checkbox" data-timetable-stop="${lineId}:${scheduleKey}:${code}" ${selectedStops.includes(code) ? "checked" : ""}>
          <span>${escapeHtml(getStopName(code))}</span>
        </label>`
            )
            .join("") ||
          `<div class="empty-mini">Nessuna fermata disponibile per questa selezione.</div>`
        }
      </div>
    </div>
  `;
}

export function collectCandidates(...groups) {
  return [...new Set(groups.flat().filter(Boolean))];
}

export function collectStopsFromTrips(trips) {
  const stops = [];
  for (const trip of trips) {
    for (const code of Object.keys(trip.stops || {})) {
      if (!stops.includes(code)) stops.push(code);
    }
  }
  return stops;
}

export function renderStopOptions(candidates, selected) {
  return candidates
    .map(
      (code) =>
        `<option value="${escapeHtml(code)}" ${code === selected ? "selected" : ""}>${escapeHtml(getStopName(code))}</option>`
    )
    .join("");
}

export function labelDay(dayType) {
  return { weekday: "Feriale", saturday: "Sabato", sunday: "Festivo" }[dayType] || dayType;
}
